import React, { useState } from "react";
import { Loader2, Pencil, Send, X, Clock3, XCircle } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/api";

/**
 * A read-only field whose modification requires admin validation.
 * Submitting creates a change request; the old value stays active until approval.
 */
export function RequestableField({ field, label, value, display, type = "text", options, pending, rejected, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!val.trim()) { toast.error("Saisissez la nouvelle valeur"); return; }
    setBusy(true);
    try {
      const { data } = await apiClient.post("/my/change-requests", { field, new_value: val.trim() });
      toast.success("Demande envoyée — en attente de validation NelDaari");
      if (data.warning) toast.warning(data.warning, { duration: 6000 });
      setEditing(false);
      setVal("");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const cancelRequest = async () => {
    setBusy(true);
    try {
      await apiClient.delete(`/my/change-requests/${pending.request_id}`);
      toast.success("Demande annulée");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const fmtDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="py-3 border-b border-sand/70 last:border-0" data-testid={`req-field-${field}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-mute">{label}</div>
          <div className="font-semibold text-sm mt-0.5 break-words" data-testid={`req-field-value-${field}`}>{display || value || "—"}</div>
        </div>
        {!pending && !editing && (
          <button
            type="button"
            onClick={() => { setEditing(true); setVal(value || ""); }}
            data-testid={`req-field-edit-${field}`}
            className="inline-flex items-center gap-1 text-xs rounded-full border border-sand hover:border-ink px-3 py-1.5 font-semibold shrink-0"
          >
            <Pencil className="w-3 h-3" /> Modifier
          </button>
        )}
      </div>

      {pending && (
        <div className="mt-2 rounded-xl bg-brand-yellow/15 border border-brand-yellow/50 px-3 py-2 text-xs" data-testid={`req-field-pending-${field}`}>
          <div className="flex items-center gap-1.5 font-semibold text-[#8A6D00]">
            <Clock3 className="w-3.5 h-3.5" /> Modification demandée le {fmtDate(pending.created_at)} — en attente de validation
          </div>
          <div className="text-mute mt-1">Nouvelle valeur demandée : <b className="text-ink">{pending.new_value}</b></div>
          <button
            type="button"
            onClick={cancelRequest}
            disabled={busy}
            data-testid={`req-field-cancel-${field}`}
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-brand-red underline underline-offset-2 disabled:opacity-50"
          >
            <X className="w-3 h-3" /> Annuler ma demande
          </button>
        </div>
      )}

      {!pending && rejected && (
        <div className="mt-2 rounded-xl bg-brand-red/5 border border-brand-red/30 px-3 py-2 text-xs" data-testid={`req-field-rejected-${field}`}>
          <div className="flex items-center gap-1.5 font-semibold text-brand-red">
            <XCircle className="w-3.5 h-3.5" /> Demande rejetée le {rejected.decided_at ? fmtDate(rejected.decided_at) : ""}
          </div>
          <div className="text-mute mt-0.5">Motif : {rejected.reason || "non précisé"}</div>
        </div>
      )}

      {editing && !pending && (
        <div className="mt-2 flex items-center gap-2">
          {options ? (
            <select
              value={val}
              onChange={(e) => setVal(e.target.value)}
              data-testid={`req-field-input-${field}`}
              className="flex-1 bg-elevated rounded-xl px-3 py-2.5 text-sm outline-none border border-sand focus:border-brand-red"
            >
              <option value="">— Choisir —</option>
              {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : (
            <input
              type={type}
              value={val}
              onChange={(e) => setVal(e.target.value)}
              data-testid={`req-field-input-${field}`}
              className="flex-1 bg-elevated rounded-xl px-3 py-2.5 text-sm outline-none border border-sand focus:border-brand-red"
            />
          )}
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid={`req-field-submit-${field}`}
            className="inline-flex items-center gap-1 rounded-full bg-brand-red text-white px-4 py-2 text-xs font-semibold disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Demander
          </button>
          <button type="button" onClick={() => setEditing(false)} className="text-xs text-mute underline">Annuler</button>
        </div>
      )}
    </div>
  );
}
